import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { CSVLink } from "react-csv";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import axiosInstance from "../../utils/axiosConfig";
import { authService } from "../../utils/authService";
import { useNavigate } from "react-router-dom";
import { exportToPDFUser } from "./utils/exportPdfUser";
import SignatureCanvas from "react-signature-canvas";
import { useTranslation } from "react-i18next";

function UserList() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // Estado y ref para el diálogo de firma dibujada
  const [openSignatureDialog, setOpenSignatureDialog] = useState(false);
  const [selectedUserForSignature, setSelectedUserForSignature] = useState(null);
  const sigPadRef = useRef();

  // Obtener el usuario logueado (asumiendo que authService.getUser() devuelve el usuario actual)
  const loggedInUser = authService.getUserData();
  const loggedInUserId = loggedInUser?.id;

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch de usuarios desde la API
  const fetchUsers = async () => {
    try {
      const token = authService.getToken();
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // Manejar la eliminación de un usuario
  const handleDeleteUser = async (userId) => {
    // Opcional: Validar en el front que no se intente eliminar al usuario logueado
    if (userId === loggedInUserId) return;

    if (!window.confirm(t("deleteUserConfirmation"))) return;

    try {
      const token = authService.getToken();
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  // Manejar carga de firma desde archivo
  const handleSignatureUpload = async (userId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("signature", file);

    try {
      const token = authService.getToken();
      await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/users/${userId}/set_signature`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchUsers();
    } catch (error) {
      console.error("Error uploading signature:", error);
    }
  };

  // Abrir el diálogo para dibujar la firma de un usuario
  const openDrawSignatureDialog = (user) => {
    setSelectedUserForSignature(user);
    setOpenSignatureDialog(true);
  };

  // Cerrar el diálogo de firma y limpiar el canvas
  const closeSignatureDialog = () => {
    setOpenSignatureDialog(false);
    setSelectedUserForSignature(null);
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
  };

  // Helper para convertir dataURL a Blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Guardar la firma dibujada en la API
  const handleDrawSignatureSave = async () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const signatureData = sigPadRef.current.toDataURL();
      const blobData = dataURLtoBlob(signatureData);
      const formData = new FormData();
      formData.append("signature", blobData, "signature.png");

      try {
        const token = authService.getToken();
        await axiosInstance.patch(
          `${process.env.REACT_APP_API_URL}/users/${selectedUserForSignature.id}/set_signature`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        closeSignatureDialog();
        fetchUsers();
      } catch (error) {
        console.error("Error saving drawn signature:", error);
      }
    }
  };

  // Filtrar usuarios según el término de búsqueda
  const filteredData = useMemo(
    () =>
      users.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.role.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [users, searchTerm]
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Controles de búsqueda, exportación y creación de usuario */}
      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            placeholder={t("searchUsers")}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          display="flex"
          gap={1}
          justifyContent="flex-end"
        >
          <CSVLink
            data={filteredData}
            filename="users.csv"
            style={{ textDecoration: "none" }}
          >
            <Button variant="outlined" startIcon={<DownloadIcon />}>
              {t("exportCSV")}
            </Button>
          </CSVLink>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportToPDFUser(filteredData)}
          >
            {t("exportPDF")}
          </Button>
        </Grid>

        <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/user-management")}
          >
            {t("createUser")}
          </Button>
        </Grid>
      </Grid>

      {/* Tabla de Usuarios */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("name")}</TableCell>
              <TableCell>{t("email")}</TableCell>
              <TableCell>{t("role")}</TableCell>
              <TableCell>{t("document")}</TableCell>
              <TableCell>{t("actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{user.document}</TableCell>
                <TableCell sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/user-management/${user.id}`)}
                  >
                    {t("edit")}
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    id={`signature-upload-${user.id}`}
                    style={{ display: "none" }}
                    onChange={(e) => handleSignatureUpload(user.id, e)}
                  />
                  <label htmlFor={`signature-upload-${user.id}`}>
                    <Button
                      component="span"
                      startIcon={<FileUploadIcon />}
                      size="small"
                      variant="contained"
                    >
                      {t("uploadSignature")}
                    </Button>
                  </label>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => openDrawSignatureDialog(user)}
                  >
                    {t("drawSignature")}
                  </Button>
                  {/* Botón de eliminación (no se muestra si es el usuario logueado) */}
                  {user.id !== loggedInUserId && (
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      {t("delete")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo para dibujar firma */}
      <Dialog
        open={openSignatureDialog}
        onClose={closeSignatureDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("drawSignatureFor", { name: selectedUserForSignature?.name })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #ccc", borderRadius: "4px" }}>
            <SignatureCanvas
              ref={sigPadRef}
              canvasProps={{ style: { width: "100%", height: "200px" } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSignatureDialog}>{t("cancel")}</Button>
          <Button onClick={() => sigPadRef.current?.clear()}>{t("clear")}</Button>
          <Button variant="contained" onClick={handleDrawSignatureSave}>
            {t("saveSignature")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserList;
